import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AsmAuthService, AsmAuthStorageService } from '@spartacus/asm/root';
import {
    AuthActions,
    AuthMultisiteIsolationService,
    AuthRedirectService, FeatureConfigService,
    GlobalMessageService,
    OAuthLibWrapperService, OAuthTryLoginResult, OCC_USER_ID_CURRENT,
    OccEndpointsService,
    RoutingService,
    StateWithClientAuth,
    UserIdService
} from '@spartacus/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Version of AuthService that is working for both user na CS agent.
 * Overrides AuthService when ASM module is enabled.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomAsmAuthService extends AsmAuthService {
  private featureConfig = inject(FeatureConfigService);
  private occEndpointsService = inject(OccEndpointsService);
  private http = inject(HttpClient);

  constructor(
    protected store: Store<StateWithClientAuth>,
    protected userIdService: UserIdService,
    protected oAuthLibWrapperService: OAuthLibWrapperService,
    protected authStorageService: AsmAuthStorageService,
    protected authRedirectService: AuthRedirectService,
    protected globalMessageService: GlobalMessageService,
    protected routingService: RoutingService,
    protected authMultisiteIsolationService?: AuthMultisiteIsolationService
  ) {
    super(
      store,
      userIdService,
      oAuthLibWrapperService,
      authStorageService,
      authRedirectService,
      globalMessageService,
      routingService,
      authMultisiteIsolationService
    );
  }

  override warnAboutLoggedCSAgent(): void {
    // Override message
  }

  override async checkOAuthParamsInUrl(): Promise<void> {
    try {
      const url = new URL(window.location.href);
      // Omit try login if we are on setpassword page
      if (window.location.pathname.includes('/login/pw/setpassword') && url.searchParams.has('code')) {
        return;
      }

      const loginResult: OAuthTryLoginResult = await this.oAuthLibWrapperService.tryLogin();

      const token = this.authStorageService.getItem('access_token');

      const isEmulated = await firstValueFrom(this.userIdService.isEmulated());

      const isUsingASMClient = await firstValueFrom(this.isUsingASMClient());

      // We get the value `true` of `result` in the _code flow_ even if we did not log in successfully
      // tslint:disable-next-line:max-line-length
      // (see source code https://github.com/manfredsteyer/angular-oauth2-oidc/blob/d95d7da788e2c1390346c66de62dc31f10d2b852/projects/lib/src/oauth-service.ts#L1711),
      // that why we also need to check if we have access_token
      if (loginResult.result && token && !isEmulated && !isUsingASMClient) {
        this.userIdService.setUserId(OCC_USER_ID_CURRENT);

        if (!this.featureConfig.isEnabled('dispatchLoginActionOnlyWhenTokenReceived')) {
          // When the feature flag is disabled, dispatch the login action even when the token
          // is retrieved from storage (e.g., page refresh)
          this.store.dispatch(new AuthActions.Login());
        }
        // We check if the token was received during the `tryLogin()` attempt.
        // If so, we will redirect as we can deduce we are returning from the authentication server.
        // Redirection should not be done in cases we get the token from storage (eg. refreshing the page).
        // In this case we need to dispatch the login action to indicate that the user has just logged in.
        if (loginResult.tokenReceived) {
          if (this.featureConfig.isEnabled('dispatchLoginActionOnlyWhenTokenReceived')) {
            this.store.dispatch(new AuthActions.Login());
          }
          this.authRedirectService.redirect();
        }
      }
    } catch {}
  }

  override refreshAuthConfig(): void {
    if (!!this.featureToggles.authorizationCodeFlowByDefault && this.isAsmEnabled()) {
      this.oAuthLibWrapperService.changeAuthConfigClientId(environment.authclientASMId);
      this.updateIsUsingASMClient(true);
      return;
    }

    this.oAuthLibWrapperService.refreshAuthConfig();
    this.updateIsUsingASMClient(false);
  }

  sendLoginNotification(userId: string): void {
    const url = this.occEndpointsService.buildUrl(
      `/users/${userId}/loginnotification`
    );
    this.http.post(url, {}).subscribe();
  }
}
